import React, {ChangeEvent, Fragment, useEffect, useMemo, useState} from "react";
import {Button, Form, Table} from "react-bootstrap";
import {
    CellContext, Column,
    ColumnDef, ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel, getFilteredRowModel,
    PaginationState,
    Row as RowInfo,
    SortingState,
    useReactTable
} from "@tanstack/react-table";
import {Banner, BannersDataQuery, BannerStore} from "Stores/bannerStore";

import {inject, observer} from "mobx-react";
import {reaction} from "mobx";
import {Utils, Defaults} from "../../../common/utils";
import BannerForm from "Components/Super/Schools/BannerForm";
import {School, SchoolStore} from "Stores/superStore/schools";
import DebouncedInput from "Components/Common/DebouncedInput";
import {appStore} from "Stores/index";
import PageTitle from "Components/Common/PageTitle";
import {useInternalContext} from "@/contexts/internal";

interface BannersListPageProps {
    bannerStore?: BannerStore
    schoolStore?: SchoolStore
}

@inject("bannerStore", "schoolStore")
@observer
export class BannersListPage extends React.Component<BannersListPageProps, any>{
    render() {
        if (!this.props.bannerStore || !this.props.schoolStore) {
            return null;
        }
        return (
            <BannersList bannerStore={this.props.bannerStore} schoolStore={this.props.schoolStore} />
        );
    }
}

interface BannersListProps {
    bannerStore: BannerStore
    schoolStore: SchoolStore
}

function BannersList({bannerStore, schoolStore}: BannersListProps) {
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [bannersDataQuery, setBannersDataQuery] = useState<BannersDataQuery | null>(null);
    const {setRenderActionButton} = useInternalContext();
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [schools, setSchools] = useState<School[]>([]);
    const [{ pageIndex, pageSize }, setPagination] =
        useState<PaginationState>({
            pageIndex: 0,
            pageSize: Defaults.tablePageSize(),
        });
    const pagination = useMemo(
        () => ({
            pageIndex,
            pageSize,
        }),
        [pageIndex, pageSize]
    );
    const columns: ColumnDef<Banner>[] = [
        {
            header: "Title",
            accessorKey: "banner_text",
        }, {
            header: "Text",
            accessorKey: "description",
        }, {
            header: "Enabled",
            accessorKey: "enabled",
        }, {
            header: "Start date",
            accessorFn: (banner) => banner.start_date ? Utils.formatDate(new Date(banner.start_date), "MM/DD/YYYY hh:mm:ss A") : "",
            enableSorting: false,
            enableColumnFilter: false
        }, {
            header: "End date",
            accessorFn: (banner) => banner.end_date ? Utils.formatDate(new Date(banner.end_date), "MM/DD/YYYY hh:mm:ss A"): "",
            enableSorting: false,
            enableColumnFilter: false
        }, {
            id: "schools",
            header: "Schools",
            accessorFn: (banner) => banner.schools.map(school => school.school_id).join(","),
            cell: (props) => {
                return (
                    <div className="text-truncate" style={{maxWidth: "100px"}}>
                        {props.row.original.schools.map(school => school.school_id).join(", ")}
                    </div>
                );
            },
            enableSorting: false
        }, {
            id: "expander",
            header: () => null,
            enableColumnFilter: false,
            cell: (props) => {
                return (
                    <div>
                        <button
                            className="table-action-button"
                            onClick={ props.row.getToggleExpandedHandler() }>
                            <i className={`icon icon-${props.row.getIsExpanded() ? "compress" : "expand"}`} />
                        </button>
                    </div>
                );
            }
        }, {
            id: "deleter",
            header: () => null,
            enableColumnFilter: false,
            cell: (props: CellContext<Banner, unknown>) => {
                const onClick = () => {
                    handleDelete(props.row.original);
                };
                return (
                    <div>
                        <button className="table-action-button" onClick={onClick}>
                            <i className="icon icon-times" aria-hidden="true" />
                        </button>
                    </div>
                );
            }
        }
    ];
    const table = useReactTable({
        columns,
        data: bannersDataQuery?.data ?? [],
        pageCount: Utils.getPageCount(bannersDataQuery?.result?.totalCount ?? 0, pageSize),
        state: {
            pagination,
            sorting,
            columnFilters
        },
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getRowCanExpand: () => true,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true
    });

    const handleDelete = async (banner: Banner) => {
        appStore.showConfirmModal({
            element: <span>Are you sure you want to delete this banner?</span>,
            accept: async () => {
                await bannerStore.deleteBanner(banner.ruuid);
                setRefreshKey(prev => prev + 1);
            },
            cancel: () => {},
            showActions: true
        });
    };

    const getEditBannerHandler = (row: RowInfo<Banner>) => {
        return async (banner: Banner, schoolIds: string[], schoolTypes: string[], dataSources: string[], ruuid?: string) => {
            if (ruuid) {
                // update
                await bannerStore.updateBanner(ruuid, banner, schoolIds, schoolTypes, dataSources);
                row.getToggleExpandedHandler()();
                setRefreshKey(prevState => prevState + 1);
            }
        };
    };

    const handleAddBanner = async (banner: Banner, schoolIds: string[], schoolTypes: string[], dataSources: string[]) => {
        await bannerStore.addBanner(banner, schoolIds, schoolTypes, dataSources);
        setRefreshKey(prevState => prevState + 1);
        setShowCreateForm(false);
    };

    const onNextPage = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        table.nextPage();
    };

    const onPreviousPage = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        table.previousPage();
    };

    const handleCreateClick = () => {
        setShowCreateForm(true);
    };

    const renderCreateButton = () => {
        return (
            <div className="actions">
                <Button onClick={ handleCreateClick }>create notification</Button>
            </div>
        );
    };

    useEffect(() => {
        reaction(() => bannerStore.bannersManagement, newBanners => {
            setBannersDataQuery({
                data: newBanners,
                result: {totalCount: bannerStore.totalBannersManagement}
            });
        });
    }, []);

    React.useEffect(() => {
        // Set the renderActionButton when this component is mounted
        setRenderActionButton(renderCreateButton());

        // Clear the renderActionButton when this component is unmounted
        return () => {
            setRenderActionButton(null); // Clean up
        };
    }, []);

    useEffect(() => {
        let sortBy, sortOrder;
        if (sorting.length > 0) {
            sortBy = sorting[0].id;
            sortOrder = sorting[0].desc ? "desc" : "asc";
        }
        let filters;
        if (columnFilters.length > 0) {
            filters = {};

            columnFilters.forEach(filter => {
                if (filter.id === "schools") {
                    if (filter.value === "all") {
                        return;
                    } else {
                        filters[filter.id] = {operator: "eq", value: filter.value};
                    }
                } else if (filter.id === "enabled" || filter.id === "admins" || filter.id === "whats_new") {
                    if (filter.value === "all") {
                        return;
                    } else {
                        filters[filter.id] = {operator: "eq", value: filter.value === "true"};
                    }
                } else {
                    filters[filter.id] = {operator: "contains", value: filter.value};
                }
            });
        }
        bannerStore.fetchBannersManagement({sortBy, sortOrder, page: pageIndex, pageSize, filters});
    },[sorting, pageIndex, refreshKey, columnFilters]);

    useEffect(() => {
        reaction(() => schoolStore.schools, newSchools => {
            setSchools(newSchools);
        });
    }, []);

    useEffect(() => {
        schoolStore.fetchSchools();
    }, []);

    const renderTable = () => {
        return (
            <>
                <Table responsive>
                    <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th key={header.id}>
                                    {header.isPlaceholder
                                        ? null
                                        : (
                                            <>
                                                <div
                                                    className={header.column.getCanSort()
                                                        ? "cursor-pointer " + (header.column.getIsSorted() || "")
                                                        : ""}
                                                    onClick={header.column.getToggleSortingHandler()}>
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                                </div>
                                                {(header.column.getCanFilter() && (
                                                    <div>
                                                        <Filter column={header.column} schools={schools} />
                                                    </div>) || <div className="fill-no-filter"></div>)}
                                            </>
                                        )}
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>
                    <tbody>
                    {table.getRowModel().rows.map(row => (
                        <Fragment key={row.id}>
                            <tr>
                                {row.getVisibleCells().map(cell => (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                            {row.getIsExpanded() && (
                                <tr>
                                    <td colSpan={row.getVisibleCells().length}>
                                        <div className="subcomponent-row">
                                            <BannerForm
                                                onCancel={row.getToggleExpandedHandler()}
                                                onSave={getEditBannerHandler(row)}
                                                banner={row.original}
                                                schoolStore={schoolStore}
                                                bannerStore={bannerStore}/>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </Fragment>
                    ))}
                    {table.getRowModel().rows.length === 0 && (<tr><td colSpan={7} className="text-center">No Banners Available</td></tr>)}
                    </tbody>
                </Table>
                <div className={"pagination-bottom"}>
                    <div className={"pagination"}>
                        <div className={"previous d-grid gap-2"}>
                            <Button disabled={!table.getCanPreviousPage()} onClick={onPreviousPage}>Previous</Button>
                        </div>
                        <div className={"center"}>
                    <span className={"page-info"}>
                        Page
                        <div className={"page-jump"}>
                            <Form.Control
                                type="number"
                                value={pageIndex + 1}
                                onChange={e => {
                                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                    table.setPageIndex(page);
                                }}/>
                        </div>
                        of{" "}
                        <span className={"total-pages"}>{table.getPageCount()}</span>
                    </span>
                        </div>
                        <div className={"next d-grid gap-2"}>
                            <Button disabled={!table.getCanNextPage()} onClick={onNextPage}>Next</Button>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    return (
        <div className="list list-container">
            <PageTitle pageTitle={"Banners"}/>
            {showCreateForm
                ? <div className="focused-form">
                    <BannerForm
                        onCancel={() => setShowCreateForm(false)}
                        onSave={handleAddBanner}
                        schoolStore={schoolStore}
                        bannerStore={bannerStore} />
                    </div>
                : renderTable()}
        </div>
    );
}

interface FilterProps {
    column: Column<any, unknown>
    schools: School[]
}

function Filter({column, schools}: FilterProps) {
    const columnFilterValue = column.getFilterValue();
    const onChangeSelect = (e: ChangeEvent<HTMLSelectElement>) => {
        column.setFilterValue(() => e.target.value);
    };

    const onChangeInput = (value: string | number) => {
        column.setFilterValue(value);
    };

    if (column.id === "schools") {
        return (
            <Form.Select onChange={onChangeSelect}>
                <option value="all">All</option>
                {schools.map(school => <option key={school.school_id} value={school.school_id}>{school.school_id}</option>)}
            </Form.Select>
        );
    }

    if (column.id === "enabled" || column.id === "admins" || column.id === "whats_new") {
        return (
            <Form.Select onChange={onChangeSelect}>
                <option value="all">all</option>
                <option value="true">true</option>
                <option value="false">false</option>
            </Form.Select>
        );
    }

    return <DebouncedInput type={"text"} value={(columnFilterValue ?? "") as string} onChange={onChangeInput} />;
}